import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Ticket, FolderKanban, CheckSquare, ClipboardList,
  BarChart3, Settings, Users, GitBranch, Palette, Headphones, ScrollText, MessageSquareText, Clock, BookOpen, LucideIcon,
  ArrowRightLeft,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePendingCount } from '../hooks/useTickets';
import { useTenant } from '../context/TenantContext';
import type { Role } from '../types';
import { getUserRoles, hasRole } from '../utils/roleUtils';
import { classNames } from '../utils/helpers';

interface NavItem { label: string; to: string; icon: LucideIcon; group: 'Support' | 'Knowledge' | 'Insights' | 'Admin'; }

const ALL_ITEMS: Record<string, NavItem> = {
  '/dashboard':   { label: 'Dashboard',   to: '/dashboard',   icon: LayoutDashboard, group: 'Support' },
  '/all-tickets': { label: 'All Tickets', to: '/all-tickets', icon: FolderKanban,    group: 'Support' },
  '/tickets':     { label: 'My Tickets',  to: '/tickets',     icon: Ticket,          group: 'Support' },
  '/workspace':   { label: 'Workspace',   to: '/workspace',   icon: Headphones,      group: 'Support' },
  '/approvals':   { label: 'Approvals',   to: '/approvals',   icon: CheckSquare,     group: 'Support' },
  '/tasks':       { label: 'Tasks',       to: '/tasks',       icon: ClipboardList,   group: 'Support' },
  '/kb':          { label: 'Knowledge Base', to: '/kb',       icon: BookOpen,        group: 'Knowledge' },
  '/reports':     { label: 'Reports',     to: '/reports',     icon: BarChart3,       group: 'Insights' },
  '/assignment-log': { label: 'Assignment Log', to: '/assignment-log', icon: ArrowRightLeft, group: 'Insights' },
  '/admin':       { label: 'Settings',    to: '/admin',       icon: Settings,        group: 'Admin' },
  '/admin/employees':       { label: 'Employees',       to: '/admin/employees',       icon: Users,     group: 'Admin' },
  '/admin/approval-chains': { label: 'Approval Chains', to: '/admin/approval-chains', icon: GitBranch, group: 'Admin' },
  '/admin/branding':        { label: 'Branding',        to: '/admin/branding',        icon: Palette,   group: 'Admin' },
  '/admin/audit':           { label: 'Audit Log',       to: '/admin/audit',           icon: ScrollText, group: 'Admin' },
  '/admin/canned-responses':{ label: 'Canned Replies',  to: '/admin/canned-responses', icon: MessageSquareText, group: 'Admin' },
  '/admin/sla-policies':    { label: 'SLA Policies',    to: '/admin/sla-policies',    icon: Clock,     group: 'Admin' },
};

const ITEMS_BY_ROLE: Record<Role, string[]> = {
  employee:       ['/dashboard', '/tickets', '/kb'],
  line_manager:   ['/dashboard', '/tickets', '/approvals', '/kb'],
  it_staff:       ['/dashboard', '/tickets', '/workspace', '/tasks', '/kb'],
  it_manager:     ['/dashboard', '/all-tickets', '/workspace', '/approvals', '/tasks', '/kb', '/reports', '/assignment-log'],
  top_management: ['/dashboard', '/approvals', '/kb'],
  admin:          ['/dashboard', '/all-tickets', '/workspace', '/approvals', '/tasks', '/kb', '/reports', '/assignment-log', '/admin', '/admin/employees', '/admin/approval-chains', '/admin/sla-policies', '/admin/canned-responses', '/admin/branding', '/admin/audit'],
};

const ORDER = ['/dashboard', '/all-tickets', '/tickets', '/workspace', '/approvals', '/tasks', '/kb', '/reports', '/assignment-log', '/admin', '/admin/employees', '/admin/approval-chains', '/admin/sla-policies', '/admin/canned-responses', '/admin/branding', '/admin/audit'];
const GROUPS: NavItem['group'][] = ['Support', 'Knowledge', 'Insights', 'Admin'];

function navFor(roles: string[]): NavItem[] {
  const paths = new Set<string>();
  for (const r of roles) (ITEMS_BY_ROLE[r as Role] ?? []).forEach(p => paths.add(p));
  return ORDER.filter(p => paths.has(p)).map(p => ALL_ITEMS[p]);
}

interface SidebarProps { open: boolean; onClose: () => void; }

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { user } = useAuth();
  const tenant = useTenant();
  // Ticket detail (/tickets/:id) is reached from several tabs (My Tickets, All Tickets,
  // Workspace, Approvals). NavLink's prefix matching would otherwise always highlight
  // "My Tickets" there, regardless of which tab the viewer actually came from. Pages
  // that navigate to a ticket pass `state: { activeNav }` to say which tab should stay
  // highlighted; with no state (direct link, refresh), nothing force-highlights.
  const location = useLocation();
  const activeNavOverride = (location.state as { activeNav?: string } | null)?.activeNav;
  const showPendingBadge   = hasRole(user, 'it_manager') || hasRole(user, 'admin');
  const { count: pending } = usePendingCount(showPendingBadge);

  if (!user) return null;

  const items = navFor(getUserRoles(user));

  return (
    <aside
      className={classNames(
        'fixed md:relative inset-y-0 left-0 z-30 md:z-auto w-[224px] shrink-0 min-h-screen',
        'flex flex-col text-white transition-transform duration-300 ease-in-out',
        open ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
      )}
      style={{ background: 'linear-gradient(180deg, #0A1535 0%, #010E23 32%, #010E23 100%)' }}
    >
      {/* Brand */}
      <div className="px-4 py-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center font-extrabold text-brand-fg shrink-0"
               style={{ background: 'var(--brand-primary, #5B21FF)' }}>
            {(tenant.name ?? 'Service Desk').charAt(0)}
          </div>
          <div className="font-brand font-extrabold text-[16px] leading-tight truncate">
            {tenant.name ?? (<><span className="text-white">Service</span><span className="text-[#7C6FE0]">Desk</span></>)}
          </div>
        </div>
      </div>

      {/* Nav (grouped) */}
      <nav className="flex-1 overflow-y-auto py-3">
        {GROUPS.map(group => {
          const groupItems = items.filter(i => i.group === group);
          if (!groupItems.length) return null;
          return (
            <div key={group} className="mb-3">
              <div className="px-4 mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/35">{group}</div>
              {groupItems.map(item => {
                const Icon = item.icon;
                const badge = item.to === '/approvals' && showPendingBadge ? pending : 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/dashboard' || item.to === '/admin' || item.to === '/tickets'}
                    onClick={onClose}
                    className={({ isActive }) => {
                      const active = activeNavOverride ? item.to === activeNavOverride : isActive;
                      return classNames(
                        'flex items-center gap-3 px-4 py-2 text-[13px] border-l-[3px] transition-colors',
                        active
                          ? 'text-white font-semibold border-brand bg-white/[0.07]'
                          : 'text-white/65 border-transparent hover:text-white hover:bg-white/[0.04]',
                      );
                    }}
                  >
                    <Icon size={17} strokeWidth={2} />
                    <span className="flex-1">{item.label}</span>
                    {badge > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
